import { getTotalPrice } from '@shared/lib/money';
import { createSelector } from 'reselect';

import { OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { UpgradeModalState } from './types';
import { Money } from '../../../types';

import { isActualBaggage, joinServicesByRfics } from '../utils';
import { ServicePayload } from '../../../Checkout/store/selectedServices/actions';
import { SelectedServiceType } from '../../../Checkout/store/selectedServices/reducers';
import { Baggage } from '../../../BaggageService/types';
import { getGdsServiceCalculatedCountInfo } from '@shared/lib/gdsService/getGdsServiceCalculatedCountInfo';
import { buildSelectedServiceKey } from '@shared/lib/gdsService/buildSelectedServiceKey';
import { getEmptyServicesItems } from '../utils';
import { SelectedUpgrade } from '../types';

const getState = (state: UpgradeModalState) => state;

export const getServices = createSelector(getState, state => state.services);

export const getSelectedServices = createSelector(getState, state => state.selectedServices);

export const getUpgradableBaggage = createSelector(getState, state => state.upgradableBaggage);

export const getPassengers = createSelector(getState, state => state.passengers);

export const hasUnconfirmedServices = createSelector(getSelectedServices, selected =>
	selected.some(service => service.count > 0 && service.count !== service.confirmedCount)
);
export const getSegmentIds = createSelector(getState, state => state.segmentIds);

export const getAvailableSegmentIds = createSelector(getServices, baggage => {
	const availableSegmentIdsSet = new Set<string>();

	baggage.forEach(baggage => {
		baggage.segmentIds.forEach(segmentIds => {
			segmentIds.forEach(segmentId => availableSegmentIdsSet.add(segmentId));
		});
	});

	return [...availableSegmentIdsSet];
});

export const getIsAllServicesCanAddedToEntireFlight = createSelector(
	[getServices, getSegmentIds],
	(services, segmentIds) => {
		const groupedServices = joinServicesByRfics(services.filter(service => service.canBeAdded));

		if (!groupedServices.length) {
			return false;
		}

		return groupedServices.every(servicesWithSameRfisc => {
			const availableSegmentsSet = new Set<string>();

			servicesWithSameRfisc.forEach(service =>
				service.segmentIds.forEach(([segmentId]) => {
					availableSegmentsSet.add(segmentId);
				})
			);

			return availableSegmentsSet.size === segmentIds.length;
		});
	}
);

export const getDifferentForReturnFlight = createSelector(
	[getState, getIsAllServicesCanAddedToEntireFlight],
	(state, isAllServicesCanAddForWholeFlight) => {
		if (isAllServicesCanAddForWholeFlight) {
			return state.differentForReturnFlight;
		}

		return true;
	}
);

export const getPrice = createSelector(
	getServices,
	getSelectedServices,
	(services, items): Money => {
		if (items.every(item => !item.count)) {
			return null;
		}

		return getTotalPrice(items, item => {
			const serviceItem = services.find(service => item.serviceId === service.id);

			if (serviceItem) {
				return {
					...serviceItem.price,
					amount: serviceItem.price.amount * item.count * item.segmentIds.length
				};
			}

			return null;
		});
	}
);

export const getDisabled = createSelector(getState, state => state.disabled);

export const getBaggageTab = createSelector(getState, state => state.baggageTab);

export const getCurrentSlide = createSelector(getState, state => state.currentSlide);

export const getActualBaggage = (
	baggage: Baggage[],
	{
		segmentId,
		passengerId,
		onlyAvailable
	}: { segmentId?: string; passengerId?: string; onlyAvailable?: boolean } = {}
) => {
	return createSelector(getSelectedServices, selectedServices => {
		const selectedServicesMap = new Map(selectedServices.map(selected => [selected.serviceId, selected]));

		let actualBaggage: Baggage = null;

		baggage.forEach(bag => {
			const segmentIdSet = new Set(bag.segmentIds.reduce((ids, segmentIds) => [...ids, ...segmentIds], []));
			const passengerIdSet = new Set(bag.allowedPassengers);

			const isIncludeSegment = !segmentId || (segmentId && segmentIdSet.has(segmentId));
			const isIncludePassenger = !passengerId || (passengerId && passengerIdSet.has(passengerId));

			const selectedService = selectedServicesMap.get(bag.id);
			const isAlreadySelected =
				selectedService &&
				selectedService.segmentIds.includes(segmentId) &&
				selectedService.passengerId === passengerId;

			const isActual = isActualBaggage(bag, actualBaggage, onlyAvailable) && !isAlreadySelected;

			if (isActual && isIncludeSegment && isIncludePassenger) {
				actualBaggage = bag;
			}
		});

		return actualBaggage;
	});
};

export const getSelectedServiceToSave = (upgradableBaggage: Baggage[]) =>
	createSelector(getSelectedServices, selectedServices => {
		const servicesToSave: ServicePayload[] = [];

		selectedServices.forEach(service => {
			const existsService = upgradableBaggage.find(baggage => service.serviceId === baggage.id);

			if (existsService) {
				const selectedService: SelectedServiceType = {
					id: existsService.id,
					type: OrderAdditionalServiceGdsServiceServiceType.Baggage,
					price: existsService.price,
					confirmedCount: 0,
					name: existsService.name
				};

				servicesToSave.push({
					passengerId: service.passengerId,
					count: service.count,
					serviceId: service.serviceId,
					service: selectedService,
					segmentId: service.segmentIds[0],
					allowedSegments: [service.segmentIds]
				});
			}
		});

		return servicesToSave;
	});

export const getPassengerBaggageState = ({
	baggage,
	passengerId,
	segmentIds,
	segmentId
}: {
	baggage: Baggage[];
	passengerId: string;
	segmentIds: string[];
	segmentId: string;
}) =>
	createSelector(getSelectedServices, selectedServices => {
		const baggageServiceIdSet = new Set<string>(baggage.map(({ id }) => id));
		const selectedServiceMap = new Map<string, SelectedUpgrade>();

		selectedServices.forEach(service => {
			const serviceId = service.serviceId;

			if (
				baggageServiceIdSet.has(serviceId) &&
				service.passengerId === passengerId &&
				(!segmentId || (segmentId && service.segmentIds.includes(segmentId)))
			) {
				const key = buildSelectedServiceKey(serviceId, service.segmentIds);
				selectedServiceMap.set(key, service);
			}
		});

		const items = getEmptyServicesItems({ baggage, segmentId, passengerId });
		const selectedItems = [...selectedServiceMap.values()];

		const { count: selectedCount, confirmedCount } = selectedItems
			.filter(item => !segmentIds.length || (segmentIds.length && item.segmentIds.includes(segmentIds[0])))
			.reduce(
				(total, selectedService) => ({
					count: +total.count + +selectedService.count,
					confirmedCount: +total.confirmedCount + +selectedService.confirmedCount
				}),
				{ count: 0, confirmedCount: 0 }
			);

		return { items, selectedItems, selectedCount, confirmedCount };
	});

export const getBaggageCalculatedRestrictionCount = ({
	baggage,
	passengerId,
	segmentId,
	segmentIds
}: {
	baggage: Baggage;
	passengerId: string;
	segmentId?: string;
	segmentIds: string[];
}) =>
	createSelector(
		getUpgradableBaggage,
		getSelectedServices,
		getBaggageServicesCalculatedCountInfo,
		(services, selectedServices, servicesCalculatedCountInfo) => {
			const isAllSegments = !segmentId;
			const baggageServicesMap = new Map(services.map(service => [service.id, service]));

			let maxCount = 3;
			let isExistRestriction = false;

			const calculatedCountInfo = getGdsServiceCalculatedCountInfo(
				passengerId,
				segmentIds[0],
				servicesCalculatedCountInfo,
				{
					rfisc: baggage?.rfics,
					subType: baggage?.type,
					type: OrderAdditionalServiceGdsServiceServiceType.Baggage
				}
			);

			if (calculatedCountInfo) {
				maxCount = calculatedCountInfo.maxCount;
				isExistRestriction = true;

				if (isAllSegments) {
					if (calculatedCountInfo.restrictionPerType === 'order') {
						maxCount = Math.floor(maxCount / segmentIds.length);
					}

					maxCount *= segmentIds.length;
				}
			}

			let filteredServices = selectedServices.filter(
				service => service.passengerId === passengerId && baggageServicesMap.has(service.serviceId)
			);

			if (segmentId && (!calculatedCountInfo || calculatedCountInfo.restrictionPerType !== 'order')) {
				filteredServices = filteredServices.filter(service => service.segmentIds.includes(segmentId));
			}

			if (calculatedCountInfo && calculatedCountInfo.restrictionValue && calculatedCountInfo.key !== 'type') {
				filteredServices = filteredServices.filter(service => {
					const baggageService = baggageServicesMap.get(service.serviceId);

					// A workaround for overriding keys in an object
					// in fact, the type in the baggage object is a subType, and rfics is an incorrect name
					const correctKey = calculatedCountInfo.key === 'rfisc' ? 'rfics' : 'type';

					return (
						!baggageService ||
						(baggageService && calculatedCountInfo.restrictionValue.includes(baggageService[correctKey]))
					);
				});
			}

			const selectedCount = filteredServices.reduce((count, service) => count + service.count, 0);

			return { maxCount, selectedCount, isExistRestriction };
		}
	);

export const getBaggageServicesCalculatedCountInfo = createSelector(
	getState,
	state => state.gdsServicesCalculatedLimitInfo
);
